import React, { useState, useEffect } from 'react';
import { Bell, Info, AlertTriangle, CheckCircle2, XCircle, Trash2, Check, Clock } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/integrations/supabase/AuthProvider";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const NotificationBell = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = async () => {
    if (!user) return;
    try {
      // Fetch notifications the user can see
      const { data: notis, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;

      // Fetch which ones have been read
      const { data: reads } = await supabase
        .from('notification_reads')
        .select('notification_id')
        .eq('user_id', user.id);
      
      const readIds = new Set(reads?.map(r => r.notification_id) || []);
      
      const enriched = (notis || []).map(n => ({
        ...n,
        isRead: readIds.has(n.id)
      }));

      setNotifications(enriched);
      setUnreadCount(enriched.filter(n => !n.isRead).length);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    
    // Subscribe to new platform-wide or specific notifications
    const channel = supabase
      .channel('schema-db-changes')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'notifications' 
      }, () => fetchNotifications())
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [user]);

  const markAsRead = async (id) => {
    try {
      const { error } = await supabase
        .from('notification_reads')
        .upsert({ user_id: user.id, notification_id: id });
      
      if (error) throw error;
      fetchNotifications();
    } catch (err) {
      console.error(err);
    }
  };

  const getIcon = (type) => {
    switch(type) {
      case 'alert': return <XCircle className="text-red-500" size={16} />;
      case 'warning': return <AlertTriangle className="text-amber-500" size={16} />;
      case 'success': return <CheckCircle2 className="text-sonas-accent" size={16} />;
      default: return <Info className="text-blue-500" size={16} />;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative h-9 w-9 rounded-xl flex items-center justify-center bg-sonas-surface2 hover:bg-sonas-surface3 border border-sonas-border transition-all">
          <Bell size={18} className={cn("text-sonas-dim", unreadCount > 0 && "text-sonas-accent")} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 bg-sonas-accent text-[10px] font-bold text-sonas-bg rounded-full flex items-center justify-center animate-in zoom-in-50">
              {unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-sonas-surface border-sonas-border w-[320px] p-2 mt-2 shadow-2xl rounded-2xl overflow-hidden">
        <div className="px-3 py-2 border-b border-sonas-border mb-2">
          <h4 className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Platform Broadcasts</h4>
        </div>
        
        <div className="max-h-[360px] overflow-y-auto sonas-scrollbar space-y-1">
          {notifications.length === 0 ? (
            <div className="py-8 text-center space-y-2 opacity-30">
              <Bell className="h-8 w-8 mx-auto" />
              <p className="text-[10px] font-bold uppercase">No Alerts</p>
            </div>
          ) : (
            notifications.map(noti => (
              <DropdownMenuItem 
                key={noti.id} 
                className={cn("p-3 rounded-xl flex flex-col items-start gap-1.5 focus:bg-white/[0.03] transition-all cursor-default", !noti.isRead && "bg-white/[0.02]")}
                onSelect={(e) => {
                  e.preventDefault();
                  if (!noti.isRead) markAsRead(noti.id);
                }}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    {getIcon(noti.type)}
                    <span className={cn("text-xs font-bold truncate max-w-[180px]", !noti.isRead ? "text-sonas-text" : "text-sonas-muted")}>
                      {noti.title}
                    </span>
                  </div>
                  {!noti.isRead && <div className="h-1.5 w-1.5 rounded-full bg-sonas-accent" />}
                </div>
                <p className="text-[11px] text-sonas-muted leading-relaxed line-clamp-2">
                  {noti.message}
                </p>
                <div className="flex items-center gap-2 mt-1">
                   <Clock size={10} className="text-sonas-dim" />
                   <span className="text-[9px] text-sonas-dim font-mono">{new Date(noti.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </DropdownMenuItem>
            ))
          )}
        </div>
        
        {notifications.length > 0 && (
          <div className="mt-2 pt-2 border-t border-sonas-border">
            <button 
              onClick={async () => {
                const unread = notifications.filter(n => !n.isRead);
                for(const n of unread) await markAsRead(n.id);
                toast.success("All cleared.");
              }}
              className="w-full h-8 text-[9px] font-bold uppercase tracking-widest text-sonas-dim hover:text-sonas-accent transition-colors"
            >
              Clear All Reads
            </button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;